import { categorizeIndexValue } from "../utils/helpers";

import metricMetadataIcon from '../assets/metric-metadata-icon-white.svg';
import indexMapIcon from '../assets/index-map.svg';

import { useEffect, useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, PercentileData, RawIndicatorData } from "../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata, fetchPercentileById } from "../utils/dashboardUtils";

interface IndexBannerProps {
    metric: DisaggregatedMetric | null;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: string;
    showMetadataIcon?: boolean;
};

const IndexBanner = ({ metric, disaggData, geoDetails, geoTag, selectionActive, svg, showMetadataIcon = true }: IndexBannerProps) => {
    const portOrigin = "*";
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    const [percentile, setPercentile] = useState<PercentileData[] | null>(null);
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    // Destructure metric properties
    // Provide default values if metric is null
    const id = metric?.id ?? 0;
    const name = metric?.name ?? "Selected Index";
    const versions = metric?.versions ?? [{ value: "-", unit: "-", percent_calc: null }];


    const val = versions[0].unit === "ratio" 
        ? `${versions[0].percent_calc?.n}/${versions[0].percent_calc?.d}` 
        : versions[0].value;

    // Ensure val is a number to pass to categorizeValue
    let numericVal: number;

    if (typeof val === 'string') {
        if (val.includes('/')) {
            // If val is a fraction (e.g., "5/10"), calculate the ratio
            const [numerator, denominator] = val.split('/').map(num => parseFloat(num.trim()));
            numericVal = denominator ? numerator / denominator : 0;
        } else {
            numericVal = parseFloat(val as string) || 0;
        }
    } else {
        numericVal = val;
    }

    // Use the categorizeValue function to get the category text
    const categoryText = percentile 
    ? categorizeIndexValue(numericVal, percentile, false) 
    : loading 
    ? "Loading..." 
    : "-";

    // Refs for metadata modal
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const modalId = `metric-metadata-modal-${id ?? 'main'}`;

    // Fetching county distribution and percentile data as soon as the component mounts
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const countyPromise = !countyDistributionData 
                    ? fetchDistributionData(id, [], null) 
                    : Promise.resolve(countyDistributionData);
                    
                const percentilePromise = !percentile 
                    ? fetchPercentileById(id) 
                    : Promise.resolve(percentile);
    
                const [countyResult, percentileResult] = await Promise.all([countyPromise, percentilePromise]);
    
                if (!countyDistributionData) setCountyDistributionData(countyResult || null);
                if (!percentile) setPercentile(percentileResult?.data || null);
            } catch (error) {
                console.error('Error fetching data:', error);
                setError(error as Error);
            } finally {
                setLoading(false);
            }
        };
    
        if (id && (!countyDistributionData || !percentile)) {
            fetchData();
        }
    }, [id, countyDistributionData, percentile]);


    const fetchData = async (fetchFunction: Function, setState: React.Dispatch<React.SetStateAction<any>>, dataState: any, id: number, geoPrefix?: string) => {
        if (dataState) return;  // If data is already fetched, return early

        setLoading(true);
        try {
            const result = await fetchFunction(id, geoPrefix);
            setState(result ? result.data : null);
        } catch (error) {
            setError(error as Error);
        } finally {
            setLoading(false);
        }
    };

    const handleClick = async () => {
        setShowMetadata((prev) => !prev);
    
        // Fetch metadata, state data, and national data if not already fetched
        if (!metadata) await fetchData(fetchMetricMetadata, setMetadata, metadata, id);

        // Fetch raw distribution data if not already fetched
        if (!distributionData) {
            setLoading(true); // Set loading to true while fetching
            try {
                const result = await fetchIndicatorBinCount(id, geoDetails, geoTag); // Provide orgId, dashboardView, and geoTag
                setDistributionData(result || null); // Update state with fetched data
            } catch (error) {
                console.error('Error fetching distribution data:', error);
            } finally {
                setLoading(false); // Set loading to false after fetching
            }
        }
    };

    // handleClickIndexIcon - Handle click event for sending message contract to map application, including the index layer id and name to toggle an index map layer
    const handleClickIndexIcon = async () => {
        // Send message to the parent window
        window.parent.postMessage(
            {
                message: "toggleOverlayLayer",
                data: {
                    id,
                    name,
                },
            },
            portOrigin
        );
        console.log("Toggle index map layer message sent:", { id, name });
    };

    return (
        <>
            <div className="flex items-center justify-between w-full h-12 bg-[#4F92B1] px-4">
                {/* Index map icon button */}
                <button
                    type="button"
                    onClick={handleClickIndexIcon}
                    title="Activate the map button to display the current index data on the map."
                    aria-label="Activate the map button to display the current index data on the map"
                    className="
                    inline-flex h-6 w-6 items-center justify-center rounded
                    transition-transform hover:scale-105 focus:scale-105
                    focus-visible:outline-none
                    focus-visible:ring-2 focus-visible:ring-offset-2
                    focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                    dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
                    "
                >
                    <img
                    src={indexMapIcon}
                    alt=""
                    aria-hidden="true"
                    className="h-6 w-6"
                    draggable="false"
                    />
                </button>

                <h1 className="text-white text-sm">The {name} is <strong>{categoryText}</strong></h1>

                {showMetadataIcon && 
                    <button
                        type="button"
                        onClick={handleClick}
                        aria-haspopup="dialog"
                        aria-expanded={showMetadata ? 'true' : 'false'}
                        aria-controls={modalId}
                        aria-label={`Additional information about ${name}`}
                        className="
                        inline-flex h-6 w-6 items-center justify-center rounded
                        transition-transform hover:scale-105 focus:scale-105
                        focus-visible:outline-none
                        focus-visible:ring-2 focus-visible:ring-offset-2
                        focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                        dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
                        "
                    >
                        <img src={metricMetadataIcon} alt="" aria-hidden="true" className="h-3 w-3" draggable="false" />
                    </button>
                }
            </div>
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                svg={svg}
                loading={loading}
                error={error}
                triggerRef={triggerRef}
                modalId={modalId}
            />
        </>
    );
};


export default IndexBanner;
